import { componentByTypename } from '@/ui/utils/componentByTypename';
import { useEffect, useState } from 'react';
import { parsePriceVariant } from '@/ui/utils/parsePriceVariant';
import { CrystallizeProductTypeValues } from '@/integrations/crystallize/types/constants';
import { DoorKnobSectionCrystallizeData } from '../types/DoorKnobSectionCrystallizeData';
import { ManufacturerOption } from '../types/ManufacturerOption';
import { parseWarehouseDetails } from '../../../utils/parseWarehouseDetails';

export function useDoorKnobInitialization(data: DoorKnobSectionCrystallizeData | undefined) {
  const [manufacturerOptions, setManufacturerOptions] = useState<ManufacturerOption[]>([]);
  const doorKnobDocument = componentByTypename(data?.content, 'ItemRelationsContent')?.items?.[0];

  // Effect for initializing and updating manufacturer options
  useEffect(() => {
    const doorKnobAdapters = componentByTypename(
      doorKnobDocument?.adapters?.content,
      'ItemRelationsContent',
    )?.items?.[0];

    if (
      !doorKnobAdapters ||
      doorKnobAdapters.__typename !== 'Product' ||
      !doorKnobAdapters.variants
    ) {
      return;
    }

    const manufacturers = doorKnobAdapters.variants
      .flatMap((variant) => {
        const variantManufacturerOptions = componentByTypename(
          variant.manufacturer?.content,
          'SelectionContent',
        )?.options;
        const selectedOption = variantManufacturerOptions?.[0];
        if (selectedOption) {
          return {
            value: selectedOption.value,
            label: selectedOption.key,
            id: doorKnobAdapters.id,
            shape: doorKnobAdapters.shape.identifier,
            path: doorKnobAdapters.path!,
            knobAdapterVariant: {
              sku: variant.sku,
              name: variant.name || doorKnobAdapters.name!,
              priceVariant: parsePriceVariant(variant.priceVariant),
              isDefault: variant.isDefault || false,
              productType:
                (componentByTypename(variant.productType?.content, 'SelectionContent')?.options?.[0]
                  ?.key as CrystallizeProductTypeValues) || CrystallizeProductTypeValues.Adapter,
              images: variant.images || [],
              warehouseDetails: parseWarehouseDetails(variant.warehouseDetails) || null,
              stockCount: variant.stock || null,
              isGlobal:
                componentByTypename(variant.isGlobal?.content, 'BooleanContent')?.value || null,
            },
          };
        }

        return [];
      })
      .filter((option) => option) as ManufacturerOption[];
    // Further filter out any undefined from the flatMap results

    setManufacturerOptions(manufacturers);
  }, [doorKnobDocument]);

  return { manufacturerOptions, doorKnobDocument };
}
